/**
 * Phase classifier for multi-phase contrast-enhanced studies.
 *
 * ┌─ Sibling source of truth ──────────────────────────────────────────────┐
 * │ dev-data-processing/pipelines/cohort_creation/configs/                 │
 * │   hepatic_lesion_annotation.yaml                                       │
 * │                                                                        │
 * │ The cohort YAML is the BACKEND filter — what series get pulled into    │
 * │ the labeling worklist. The regexes below are the FRONTEND label —      │
 * │ what badge shows on a viewport and which slot a series claims in the   │
 * │ phase-dependent layout.                                                │
 * │                                                                        │
 * │ When updating this file, also update the YAML (and vice versa). A     │
 * │ keyword change on one side silently desyncs until a clinical bug       │
 * │ surfaces — e.g., the backend pulls a series the frontend can't         │
 * │ classify, and it sorts to an unexpected slot. Tracked: #390.           │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * Classification is best-effort: a series whose description doesn't match any
 * pattern returns null and won't appear in the phase tab bar.
 */
import type { Phase } from "../types/gating";

/**
 * Phase patterns tried in order. The first regex that hits wins. Order matters
 * for ambiguous descriptions (e.g., "portal venous" must match before "venous"
 * tries to match elsewhere).
 *
 * Italian variants included because hl3d pilot data ships labels like
 * F. ARTERIAL / F. PORTAL / F. TARDIA / F. EQUILIBRIO.
 *
 * Note on order-sensitive examples:
 * - `equilibrium` is tested before `delayed` so descriptions like
 *   "Delayed 5min" classify as `equilibrium` (the `5 ?min` token wins,
 *   matching the cohort YAML's timing-based bucketing). If you flip the
 *   order, all delayed-acquired equilibrium scans collapse into `delayed`.
 * - `portal_venous` is tested before `arterial` so "portal arterial"
 *   protocols don't collide with the looser `arterial` pattern.
 */
const PHASE_PATTERNS: Array<{ phase: Phase; regex: RegExp }> = [
  {
    phase: "noncontrast",
    regex: /\b(non[- ]?contrast|noncon|unenhanced|pre[- ]?contrast|plain)\b/i,
  },
  { phase: "arterial", regex: /\b(arterial|arteriosa|art phase|hap|late art|early art)\b/i },
  {
    phase: "portal_venous",
    // `60s` / `70s` are timing aliases (Italian/legacy worklists name the
    // portal-venous phase by its acquisition delay). Narrowed to require a
    // phase/delay/portal/venous suffix so we don't catch unrelated tokens
    // like "60s reformat". Re-widen if a real cohort uses bare "60s".
    regex:
      /\b(portal venous|portal|portale|venous|pvp|pv phase|(?:60|70)s\s*(?:phase|delay|portal|venous))\b/i,
  },
  {
    phase: "equilibrium",
    regex: /\b(equilibrium|equilibrio|5 ?min|10 ?min)\b/i,
  },
  {
    phase: "delayed",
    regex: /\b(delayed|tardia|tardiva|3 ?min)\b/i,
  },
];

/**
 * Classify a single series description into a phase, or null if no pattern
 * matches. Uses series_description first; fall back to protocol_name in the
 * caller if needed.
 */
export function classifyPhase(text: string | null | undefined): Phase | null {
  if (!text) return null;
  for (const { phase, regex } of PHASE_PATTERNS) {
    if (regex.test(text)) return phase;
  }
  return null;
}

/**
 * Classify a series given description + protocol fallback. Returns null if
 * neither yields a match.
 */
export function classifySeriesPhase(
  seriesDescription: string | null | undefined,
  protocolName: string | null | undefined,
): Phase | null {
  return classifyPhase(seriesDescription) ?? classifyPhase(protocolName);
}
